import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {
  fetchCompanyTerminalSettings,
  saveCompanyTerminalSettings,
} from '@/entities/terminal-settings/api';
import type {CompanyTerminalSettings} from '@/entities/terminal-settings/types';
import {Banner} from '@/shared/ui/banner';
import {Card} from '@/shared/ui/card';
import {Skeleton} from '@/shared/ui/skeleton';
import {AppText} from '@/shared/ui/text';
import {Toggle} from '@/shared/ui/toggle';
import {radii, spacing} from '@/shared/ui/theme';

/**
 * The company-wide switch for recording passes by people the terminals do not
 * recognise.
 *
 * It is labelled by what it does, not by its field name: "log_denials" means
 * nothing to an operator, while "photograph strangers and upload the frames"
 * is the decision actually being made — one about personal data. That warning
 * is on screen before the switch is touched, not after.
 *
 * Unlike the notification settings above it, this saves on toggle rather than
 * behind a Save button: it is one boolean, and a Save button for a single
 * switch is a second decision about the first one.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  card: {gap: spacing(3)},
});

export function CompanyTerminalSettingsForm({companyId}: {companyId: string}) {
  const {t} = useTranslation();
  const qc = useQueryClient();
  const key = ['company-terminal-settings', companyId];

  const q = useQuery({
    queryKey: key,
    queryFn: () => fetchCompanyTerminalSettings(companyId),
  });

  const save = useMutation({
    mutationFn: (settings: CompanyTerminalSettings) =>
      saveCompanyTerminalSettings(companyId, settings),
    onMutate: async settings => {
      await qc.cancelQueries({queryKey: key});
      const prev = qc.getQueryData<CompanyTerminalSettings>(key);
      qc.setQueryData<CompanyTerminalSettings>(key, settings);
      return {prev};
    },
    onError: (_err, _settings, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(key, ctx.prev);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({queryKey: key});
    },
  });

  if (q.isPending) {
    return <Skeleton height={140} radius={radii.lg} />;
  }

  const settings = q.data ?? {log_denials: false};

  return (
    <View style={styles.root}>
      <Card padding={4} style={styles.card}>
        <Toggle
          label={t('notifSettings.logDenials')}
          hint={t('notifSettings.logDenialsHint')}
          value={settings.log_denials}
          disabled={save.isPending}
          onChange={v => save.mutate({...settings, log_denials: v})}
        />
        <AppText variant="caption">{t('notifSettings.logDenialsWarning')}</AppText>
        {settings.log_denials ? null : (
          <AppText variant="caption">{t('notifSettings.logDenialsOffNote')}</AppText>
        )}
      </Card>
      {q.isError ? <Banner tone="error" title={t('notifSettings.saveError')} /> : null}
      {save.isError ? <Banner tone="error" title={t('notifSettings.saveError')} /> : null}
    </View>
  );
}
